import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  setEmailData,
  setPasswordData,
  clearError,
  resetToRegistrationStep,
  businessSignup,
  businessVerify,
  businessResendVerification,
  logoutUser,
} from '@/features/auth';
import { BusinessRegistrationForm } from '@/components/business-registration-form';
import { BusinessVerificationForm } from '@/components/business-verification-form';
import { isPasswordValid as checkPasswordValidity } from '@/lib/validation/password';
import { getBusinessAuthMessage } from '@/features/auth';

function getRetryAfterSeconds(value: unknown): number | undefined {
  return Number.isSafeInteger(value) && Number(value) > 0 ? Number(value) : undefined;
}

export default function RegisterScreen() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { step, email, password, codeDestination, isLoading, error } = useAppSelector((s) => s.auth);
  const redirectTarget = (location.state as { from?: string } | null)?.from || '/';

  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);

  const passwordsMatch = password === confirmPassword;
  const isPasswordValid = checkPasswordValidity(password);

  useEffect(() => {
    const locationState = location.state as { email?: string } | null;
    if (locationState?.email && locationState.email !== email) {
      dispatch(setEmailData(locationState.email));
    }
  }, [dispatch, email, location.state]);

  useEffect(() => {
    if (step !== 'complete') return;

    const emailToPass = email;

    const id = setTimeout(() => {
      navigate('/login', { state: { email: emailToPass, from: redirectTarget } });
      dispatch(logoutUser());
    }, 2000);

    return () => clearTimeout(id);
  }, [step, navigate, dispatch, email, redirectTarget]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const id = setTimeout(() => setResendCountdown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCountdown]);

  const onEmailChange = (val: string) => {
    dispatch(setEmailData(val));
    dispatch(clearError());
    setResendCountdown(0);
  };

  const onPasswordChange = (val: string) => {
    dispatch(setPasswordData(val));
    dispatch(clearError());
  };

  const onConfirmPasswordChange = (val: string) => {
    setConfirmPassword(val);
    dispatch(clearError());
  };

  const onSubmitRegistration = () => {
    if (!email || !password || !confirmPassword) return;
    if (!passwordsMatch || !isPasswordValid) return;
    if (resendCountdown > 0) return;

    const signupPromise = dispatch(businessSignup({ email, password })).unwrap();

    toast.promise(signupPromise, {
      loading: 'Creating account...',
      success: getBusinessAuthMessage('BusinessSignupAccepted', 'Check your email for the next step.'),
      error: (err) => {
        const retryAfterSeconds = getRetryAfterSeconds(err?.retryAfterSeconds);
        if (retryAfterSeconds) setResendCountdown(retryAfterSeconds);
        return getBusinessAuthMessage(err?.message, 'Registration failed');
      },
    });
  };

  const onOtpChange = (val: string) => setOtpValue(val);

  const onSubmitOtp = () => {
    if (otpValue.length !== 6) return;
    if (!email) return;

    const verifyPromise = dispatch(businessVerify({ email, code: otpValue })).unwrap();

    toast.promise(verifyPromise, {
      loading: 'Verifying code...',
      success: getBusinessAuthMessage(
        'BusinessEmailVerificationSucceeded',
        'Your email has been verified. You can now sign in.',
      ),
      error: (err) => getBusinessAuthMessage(err?.message, 'Verification failed'),
    });
  };

  const onResendOtp = () => {
    if (!email) return;
    if (resendCountdown > 0) return;

    setOtpValue('');

    const resendPromise = dispatch(businessResendVerification({ email })).unwrap();

    toast.promise(resendPromise, {
      loading: 'Sending verification code...',
      success: getBusinessAuthMessage(
        'BusinessVerificationResendAccepted',
        'If an unverified account is available, a verification code has been sent.',
      ),
      error: (err) => {
        const retryAfterSeconds = getRetryAfterSeconds(err?.retryAfterSeconds);
        if (retryAfterSeconds) setResendCountdown(retryAfterSeconds);
        return getBusinessAuthMessage(err?.message, 'Failed to resend verification code');
      },
    });
  };

  const onChangeEmail = () => {
    dispatch(resetToRegistrationStep());
    setOtpValue('');
    setResendCountdown(0);
  };

  if (step === 'complete') {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
        <div className="text-3xl">✅</div>
        <h1 className="text-xl font-semibold">Email verified</h1>
        <p className="text-muted-foreground text-sm">Redirecting you to login…</p>
      </div>
    );
  }

  if (!email) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="text-center">
          <p className="text-muted-foreground text-sm">No email provided. Please go back and enter your email.</p>
          <Link to="/login" state={{ from: redirectTarget }} className="text-primary hover:underline">
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  if (step === 'verification') {
    return (
      <BusinessVerificationForm
        email={email}
        codeDestination={codeDestination}
        otpValue={otpValue}
        isLoading={isLoading}
        hasError={!!error}
        resendCountdown={resendCountdown}
        onOtpChange={onOtpChange}
        onSubmitOtp={onSubmitOtp}
        onResendOtp={onResendOtp}
        onChangeEmail={onChangeEmail}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">Create your account</h1>
        <p className="text-muted-foreground text-sm text-balance">Enter your details to get started</p>
      </div>

      <BusinessRegistrationForm
        email={email}
        password={password}
        confirmPassword={confirmPassword}
        isLoading={isLoading}
        retryAfterSeconds={resendCountdown}
        hasError={!!error}
        errorMessage={error}
        passwordsMatch={passwordsMatch}
        isPasswordValid={isPasswordValid}
        onEmailChange={onEmailChange}
        onPasswordChange={onPasswordChange}
        onConfirmPasswordChange={onConfirmPasswordChange}
        onSubmit={onSubmitRegistration}
      />
    </div>
  );
}
